import React, { forwardRef } from 'react';
import { useSelector } from 'react-redux';

import { SearchSimilar } from '@videoblocks/react-icons';

import Tooltip from '../../../../../../builder-io/storyblocks-design-system/components/atoms/Tooltip/Tooltip';
import { StockItem } from '../../../../../../common/types/StockItemTypes';
import { DrawerType } from '../../../../SearchTypes';
import {
  selectDrawerIsOpen,
  selectDrawerSelectedItemId,
  selectDrawerType,
  selectShowAnimatedAiThumbnailButton,
} from '../../../../selectors/searchSelectors';
import ArrowIcon from '../../../filters/ArrowIcon';
import { useStockItemCompact } from '../hooks/useStockItemCompact';

export interface RelatedVideosButtonProps {
  stockItem: StockItem;
  onClick: () => void;
}

export const RelatedVideosButton = forwardRef(
  (
    { stockItem, onClick }: RelatedVideosButtonProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    const { isCompact, containerRef } = useStockItemCompact();
    const drawerIsOpen = useSelector(selectDrawerIsOpen);
    const drawerSelectedItemId = useSelector(selectDrawerSelectedItemId);
    const drawerType = useSelector(selectDrawerType);
    const showAnimatedAiThumbnailButton = useSelector(
      selectShowAnimatedAiThumbnailButton
    );

    const isSimilarVideosDrawerOpen = () =>
      [
        drawerSelectedItemId === stockItem.id,
        drawerType === DrawerType.MORE_LIKE_THIS ||
          drawerType === DrawerType.SAME_SHOOT_SAME_MODEL_SIMILAR_CLIPS,
        drawerIsOpen,
      ].every(Boolean);

    const showTooltipButton = isCompact || showAnimatedAiThumbnailButton;

    const button = (
      <div ref={containerRef} className="h-full inline-block">
        <button
          ref={ref}
          onClick={onClick}
          className="bg-gray-900 h-full bg-opacity-80 py-2.5 px-3 flex items-center text-white space-x-1.5 rounded-full cursor-pointer border-0 hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white"
        >
          <SearchSimilar className="fill-white h-4 w-4" />
          {!showTooltipButton && (
            <>
              <span className="text-sm leading-5">Related Video</span>
              <ArrowIcon
                isCollapsed={!isSimilarVideosDrawerOpen()}
                invalidSelectionMessage={''}
                className={'fill-white w-2 h-2'}
              />
            </>
          )}
        </button>
      </div>
    );

    const buttonWithTooltip = (
      <Tooltip ref={ref} title="Related Video" trigger={button} />
    );

    return showTooltipButton ? buttonWithTooltip : button;
  }
);
